import React, { memo } from 'react';
import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
} from '@mui/icons-material';
import InlineAlert from '../../../shared/components/InlineAlert';
import EmptyStateMessage from '../../../shared/components/EmptyStateMessage';
import ModalHeader from '../../../shared/components/ModalHeader';
import ModalFooter from '../../../shared/components/ModalFooter';
import DeleteConfirmDialog from '../../../shared/components/DeleteConfirmDialog';
import CustomerFormSection from './CustomerFormSection';
import { Customer } from '../../../types';
import { EntityListPanel } from '../../../shared/components/EntityListPanel';
import { useCustomerForm } from '../hooks/useCustomerForm';

interface CustomerManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCustomerUpdated?: () => void;
  selectedCustomer?: Customer | null;
}

const CustomerManagementModal: React.FC<CustomerManagementModalProps> = ({
  isOpen,
  onClose,
  onCustomerUpdated,
  selectedCustomer: initialSelectedCustomer
}) => {
  const form = useCustomerForm({
    isOpen,
    onClose,
    onCustomerUpdated,
    initialSelectedCustomerId: initialSelectedCustomer?.id,
  });

  return (
    <Dialog
      open={isOpen}
      onClose={form.handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh', maxHeight: '90vh' }
      }}
    >
      <ModalHeader icon={BusinessIcon} title="Manage Customers" onClose={form.handleClose} />

      <DialogContent sx={{ p: 0, display: 'flex', height: '100%' }}>
        {form.loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <EntityListPanel
              entities={form.customers}
              selectedEntity={form.selectedCustomer}
              isNew={form.isNewCustomer}
              saving={form.saving}
              config={{
                getPrimaryText: (customer) => customer.name,
                getSecondaryText: (customer) => customer.operations_account_id,
                getId: (customer) => customer.id,
                Icon: BusinessIcon
              }}
              addButtonText="Add New Customer"
              emptyStateText="No customers configured"
              emptyStateHelperText='Click "Add New Customer" to get started'
              onSelect={form.handleSelect}
              onAddNew={form.handleAddNew}
            />

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
              {form.error && (
                <InlineAlert severity="error" onClose={() => form.setError(null)} sx={{ m: 3, mb: 0 }}>
                  {form.error}
                </InlineAlert>
              )}
              {form.successMessage && (
                <InlineAlert severity="success" onClose={() => form.setSuccessMessage(null)} sx={{ m: 3, mb: 0 }}>
                  {form.successMessage}
                </InlineAlert>
              )}

              {(form.selectedCustomer || form.isNewCustomer) ? (
                <CustomerFormSection
                  form={form}
                  onCustomerUpdated={() => {
                    void form.loadEntities();
                  }}
                />
              ) : (
                <EmptyStateMessage
                  icon={BusinessIcon}
                  title="No customer selected"
                  description="Select a customer from the list or add a new one"
                />
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <ModalFooter
        count={form.customers.length}
        label={`customer${form.customers.length !== 1 ? 's' : ''} configured`}
        onClose={form.handleClose}
      />

      <DeleteConfirmDialog
        open={form.showDeleteConfirm}
        title="PERMANENT DELETION WARNING"
        onCancel={form.handleDeleteCancel}
        onConfirm={form.handleDeleteConfirm}
        cancelText="Cancel - Keep Customer"
        confirmText="Yes, Delete Everything"
      >
        <InlineAlert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
            THIS ACTION CANNOT BE UNDONE AND WILL PERMANENTLY DELETE:
          </Typography>
        </InlineAlert>

        <Typography variant="body1" sx={{ mb: 2 }}>
          Customer: <strong>{form.selectedCustomer?.name}</strong>
        </Typography>

        <Box sx={{ pl: 2, mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>ALL AWS accounts</strong> configured for this customer
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>ALL investigations</strong> (active, completed, and archived)
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>ALL investigation findings</strong> and knowledge items
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>ALL chat messages</strong> and conversation history
          </Typography>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            <strong>ALL WebSocket sessions</strong> and related data
          </Typography>
        </Box>

        <InlineAlert severity="warning" sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            There is NO backup and NO recovery option. All data will be permanently lost.
          </Typography>
        </InlineAlert>
      </DeleteConfirmDialog>
    </Dialog>
  );
};

export default memo(CustomerManagementModal);
